import { FC, useState } from 'react';
import styled, { css, CSSObject } from 'styled-components';

import { PREMIUM_REVIEW_TOGGLE_STYLE, PremiumReviewToggleStyleType } from '../../ahm-constants/ui';
import isFeatureEnabled from '../../utils/is-feature-enabled';
import PremiumChangePrompt from '../premium-change-prompt';
import PremiumReviewModal from '../premium-review-modal';

type PremiumReviewToggleProps = {
  hasEvenSpacing?: boolean;
  hasMargin?: boolean;
  isProductPage?: boolean;
  showBackground?: boolean;
  type?: PremiumReviewToggleStyleType;
};

type PremiumReviewToggleWrapperProps = {
  hasEvenSpacing: boolean;
  hasMargin?: boolean;
  isProductPage: boolean;
  showBackground: boolean;
  type: PremiumReviewToggleStyleType;
};

const PremiumReviewToggleWrapper = styled.div<PremiumReviewToggleWrapperProps>(
  ({ hasEvenSpacing, showBackground, isProductPage, theme, type }) => {
    const styles: CSSObject = {};

    if (showBackground) {
      styles.backgroundColor = theme.colors.black;
    }

    if (hasEvenSpacing) {
      styles.paddingTop = '1rem';
    }

    switch (type) {
      case PREMIUM_REVIEW_TOGGLE_STYLE.RIBBON:
        // No additional styles for RIBBON
        break;

      case PREMIUM_REVIEW_TOGGLE_STYLE.FLAT:
      case PREMIUM_REVIEW_TOGGLE_STYLE.FLAT_WHITE:
      case PREMIUM_REVIEW_TOGGLE_STYLE.ALTERNATIVE_PAGE:
        styles.marginBottom = isProductPage ? '0' : '1rem';
        break;

      case PREMIUM_REVIEW_TOGGLE_STYLE.SOFT_BUNDLE_PAGE:
        Object.assign(styles, {
          marginBottom: '1rem',
          order: 1,
          textAlign: 'left',
        });
        break;

      case PREMIUM_REVIEW_TOGGLE_STYLE.INLINE:
        Object.assign(styles, {
          display: 'inline-block',
          marginBottom: '0',
          marginLeft: '1rem',
          marginTop: '0',
          verticalAlign: 'middle',
        });
        break;

      case PREMIUM_REVIEW_TOGGLE_STYLE.COMPARER_PAGE:
        Object.assign(styles, {
          marginBottom: '0',
          marginTop: '1rem',
          textAlign: 'left',
        });
        break;

      default:
        // Default style already set
        break;
    }

    return css(styles);
  },
);

const PremiumReviewToggle: FC<PremiumReviewToggleProps> = ({
  hasEvenSpacing = false,
  isProductPage = false,
  hasMargin = true,
  showBackground = false,
  type = PREMIUM_REVIEW_TOGGLE_STYLE.FLAT,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
  };

  if (!isFeatureEnabled('premiumChange')) {
    return null;
  }

  return (
    <PremiumReviewToggleWrapper
      hasEvenSpacing={hasEvenSpacing}
      hasMargin={hasMargin}
      isProductPage={isProductPage}
      showBackground={showBackground}
      type={type}
    >
      <PremiumChangePrompt
        type={type}
        toggleModal={toggleModal}
        isAlternativePage={type === PREMIUM_REVIEW_TOGGLE_STYLE.ALTERNATIVE_PAGE}
      />
      <PremiumReviewModal isOpen={isModalOpen} toggleModal={toggleModal} />
    </PremiumReviewToggleWrapper>
  );
};

export default PremiumReviewToggle;

export { PremiumReviewToggle as PremiumReviewToggleForTest, PremiumReviewToggleWrapper };
